/**
 * Docked left tool palette — icon-first, grouped, keyboard-labelled.
 * Modelled on MedDream / RadiAnt sidebars.
 */
import { For, Show } from "solid-js";
import type { JSX } from "solid-js";
import {
  IconPan, IconZoomIn, IconReset, IconWindow, IconInvert, IconFlipH, IconFlipV,
  IconPreset, IconLength, IconAngle, IconCobb, IconEllipse, IconRect, IconFreehand,
  IconPlay, IconPause, IconStackUp, IconStackDown, IconCrosshair, IconMPR,
  IconSave, IconSign, IconTrash, IconHelp,
  IconText, IconArrow, IconPin,
  IconBbox, IconPolygon, IconKeypoint, IconBrush,
  Icon3D,
} from "./Icons";

export type ToolId =
  | "pan" | "zoom" | "window" | "length" | "angle" | "cobb"
  | "ellipse" | "rect" | "freehand"
  | "text" | "arrow" | "pin"
  | "bbox" | "polygon" | "keypoint" | "mask";

export interface ToolPaletteProps {
  activeTool:   ToolId | "none";
  onTool:       (t: ToolId | "none") => void;
  cinePlaying:  boolean;
  onCineToggle: () => void;
  onReset:      () => void;
  onInvert:     () => void;
  onFlipH:      () => void;
  onFlipV:      () => void;
  onClear:      () => void;
  onPreset:     () => void;
  onHelp:       () => void;
  canMeasure:   boolean;
  canSignReport: boolean;
  onSign?:       () => void;
  onSave?:       () => void;
  stackPrev?:    () => void;
  stackNext?:    () => void;
  /** 2D ↔ 3D viewport mode. When `mode3D` is true the view is showing
   *  the volume ray-marcher. */
  mode3D?:      boolean;
  onToggle3D?:  () => void;
}

export function ToolPalette(p: ToolPaletteProps) {
  return (
    <aside class="flex w-[5.5rem] shrink-0 flex-col items-center gap-1 overflow-y-auto overflow-x-hidden border-r border-ink-300 bg-ink-100 py-2">
      <Group title="View">
        <Btn active={p.activeTool === "pan"}    onClick={() => p.onTool("pan")}    k="M" label="Pan">
          <IconPan />
        </Btn>
        <Btn active={p.activeTool === "zoom"}   onClick={() => p.onTool("zoom")}   k="Z" label="Zoom">
          <IconZoomIn />
        </Btn>
        <Btn active={p.activeTool === "window"} onClick={() => p.onTool("window")} k="W" label="Window / Level">
          <IconWindow />
        </Btn>
        <Btn onClick={p.onPreset} k="P" label="W/L preset menu">
          <IconPreset />
        </Btn>
        <Btn onClick={p.onInvert} k="I" label="Invert contrast">
          <IconInvert />
        </Btn>
        <Btn onClick={p.onFlipH} k="F" label="Flip horizontal">
          <IconFlipH />
        </Btn>
        <Btn onClick={p.onFlipV} k="V" label="Flip vertical">
          <IconFlipV />
        </Btn>
        <Btn onClick={p.onReset} k="R" label="Reset view">
          <IconReset />
        </Btn>
      </Group>

      <Show when={p.canMeasure}>
        <Group title="Measure">
          <Btn active={p.activeTool === "length"}   onClick={() => p.onTool("length")}   k="L" label="Length">
            <IconLength />
          </Btn>
          <Btn active={p.activeTool === "angle"}    onClick={() => p.onTool("angle")}    k="A" label="Angle">
            <IconAngle />
          </Btn>
          <Btn active={p.activeTool === "cobb"}     onClick={() => p.onTool("cobb")}     k="⇧A" label="Cobb angle">
            <IconCobb />
          </Btn>
          <Btn active={p.activeTool === "ellipse"}  onClick={() => p.onTool("ellipse")}  k="E" label="Ellipse ROI (HU)">
            <IconEllipse />
          </Btn>
          <Btn active={p.activeTool === "rect"}     onClick={() => p.onTool("rect")}     k="B" label="Rectangle ROI">
            <IconRect />
          </Btn>
          <Btn active={p.activeTool === "freehand"} onClick={() => p.onTool("freehand")} k="X" label="Freehand">
            <IconFreehand />
          </Btn>
          <Btn onClick={p.onClear} k="C" label="Clear all annotations" danger>
            <IconTrash />
          </Btn>
        </Group>
      </Show>

      <Show when={p.canMeasure}>
        <Group title="Label">
          <Btn active={p.activeTool === "text"}  onClick={() => p.onTool("text")}  k="T" label="Text label">
            <IconText />
          </Btn>
          <Btn active={p.activeTool === "arrow"} onClick={() => p.onTool("arrow")} k="N" label="Arrow">
            <IconArrow />
          </Btn>
          <Btn active={p.activeTool === "pin"}   onClick={() => p.onTool("pin")}   k="K" label="Pin (auto A/B/C…)">
            <IconPin />
          </Btn>
        </Group>
        <Group title="ML dataset">
          <Btn active={p.activeTool === "bbox"}     onClick={() => p.onTool("bbox")}     k="O" label="Bounding box (drag)">
            <IconBbox />
          </Btn>
          <Btn active={p.activeTool === "polygon"}  onClick={() => p.onTool("polygon")}  k="G" label="Polygon (click vertices, close near start)">
            <IconPolygon />
          </Btn>
          <Btn active={p.activeTool === "keypoint"} onClick={() => p.onTool("keypoint")} k="J" label="Keypoint / landmark">
            <IconKeypoint />
          </Btn>
          <Btn active={p.activeTool === "mask"}     onClick={() => p.onTool("mask")}     k="U" label="Mask brush (left = paint, right = erase)">
            <IconBrush />
          </Btn>
        </Group>
      </Show>

      <Group title="View mode">
        <Btn active={!!p.mode3D}
             onClick={p.onToggle3D}
             k="D" label={p.mode3D ? "Back to 2D (D)" : "Open 3D volume render (D)"}>
          <Icon3D />
        </Btn>
      </Group>

      <Group title="Playback">
        <Btn onClick={p.onCineToggle} k="␣" label="Play / Pause cine">
          {p.cinePlaying ? <IconPause /> : <IconPlay />}
        </Btn>
        <Btn onClick={p.stackPrev} k="↑" label="Previous slice">
          <IconStackUp />
        </Btn>
        <Btn onClick={p.stackNext} k="↓" label="Next slice">
          <IconStackDown />
        </Btn>
      </Group>

      <Show when={p.canSignReport}>
        <Group title="Report">
          <Btn onClick={p.onSave} k="⌃S" label="Save draft">
            <IconSave />
          </Btn>
          <Btn onClick={p.onSign} k="⌃↵" label="Sign + send" accent>
            <IconSign />
          </Btn>
        </Group>
      </Show>

      <div class="mt-auto">
        <Btn onClick={p.onHelp} k="?" label="Keyboard shortcuts">
          <IconHelp />
        </Btn>
      </div>
    </aside>
  );
}

function Group(props: { title: string; children: JSX.Element }) {
  return (
    <div class="flex w-full flex-col items-center gap-1 border-b border-ink-300 px-1 pb-2 last:border-b-0">
      <div class="w-full grid grid-cols-2 place-items-center gap-1">
        {props.children}
      </div>
    </div>
  );
}

function Btn(props: {
  onClick?: () => void;
  active?: boolean;
  label: string;
  k?: string;
  accent?: boolean;
  danger?: boolean;
  children: JSX.Element;
}) {
  return (
    <button
      type="button"
      class="group relative grid h-9 w-9 place-items-center rounded-md text-ink-800 transition"
      classList={{
        "bg-brand text-white ring-1 ring-brand/60": !!props.active,
        "hover:bg-ink-200 hover:text-white":        !props.active,
        "!text-brand":                              !!props.accent && !props.active,
        "!text-danger":                             !!props.danger,
      }}
      onClick={props.onClick}
      aria-label={props.label}
      title={`${props.label}${props.k ? "  ·  " + props.k : ""}`}
    >
      {props.children}
      <span class="pointer-events-none absolute left-full top-1/2 z-20 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-ink-0 px-2 py-1 text-[10px] text-ink-900 opacity-0 shadow-panel transition group-hover:opacity-100">
        {props.label}
        {props.k && <kbd class="ml-1.5">{props.k}</kbd>}
      </span>
    </button>
  );
}
